import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useShallowAppSelector } from 'store/hooks';
import { useHistory } from 'react-router-dom';
import T from 'components/common/text/T';
import {
  isMasterLevelUserSelector,
  isManagerLevelUserSelector,
  isNormalLevelUserSelector,
} from 'store/modules/user';
import useInput from 'lib/hooks/useInput';
import { AppActions, UserActions, UtilActions, StatisticsActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import { ICurrency, ICountry } from 'lib/types/sharedTypes';
import { IUserProfile } from 'lib/types/userTypes';
import { icon_add_kpi } from 'components/base/images';
import cx from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import CustomSpan from 'components/common/text/CustomSpan';
import KpiChartBox from 'components/kpi/KpiChartBox';
import moment from 'moment';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { pageUrl } from 'lib/mapper';
import { useTranslation, Trans } from 'react-i18next';
import CircularLoading from 'components/base/loading/CircularLoading';
import { color } from 'styles/utils';
import PageTitle, { PageSubTitle } from 'components/base/title/PageTitle';
import { oemState } from 'lib/store';

function KpiContainer() {
  const {
    language,
    fetchStatisticsList: { list: statisticsList },
    fetchStatisticsListSuccess,
    fetchStatisticsListPending,
    deleteStatisticSuccess,
  }: {
    language: string;
    fetchStatisticsList: { list: any };
    fetchStatisticsListSuccess: boolean;
    fetchStatisticsListPending: boolean;
    deleteStatisticSuccess: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    fetchStatisticsList: state.statistics.fetchStatisticsList.data || {},
    fetchStatisticsListSuccess: state.statistics.fetchStatisticsList.success,
    fetchStatisticsListPending: state.statistics.fetchStatisticsList.pending,
    deleteStatisticSuccess: state.statistics.deleteStatistic.success,
  }));
  const { t } = useTranslation();
  const history = useHistory();
  const periodRangeIndex = [5, 4, 3, 2, 1, 0]; // 기간 계산 시, 기간 index
  const dailyRangeIndex = [6, 5, 4, 3, 2, 1, 0]; // 일별 기간 계산 시, 기간 index (7일)
  const [periodMonthly, setPeriodMonthly] = useState<object[]>([]);
  const [periodWeekly, setPeriodWeekly] = useState<object[]>([]);
  const [periodDaily, setPeriodDaily] = useState<object[]>([]);
  // const prevSixMonth = moment().subtract(5, 'months').format('YYYY-MM');
  const weekCount = moment().diff(moment().startOf('y'), 'weeks') + 1;

  const [graphCount, setGraphCount] = useState<number>(0);
  // const [weekCount, setWeekCount] = useState<number>(
  //   moment().diff(moment().startOf('y'), 'weeks') + 1,
  // );

  // const prjectGraphList = [
  //   { groupDate: '2022-01', totalCount: 1 },
  //   { groupDate: '2022-02', totalCount: 5 },
  //   { groupDate: '2022-03', totalCount: 11 },
  //   { groupDate: '2022-04', totalCount: 21 },
  //   { groupDate: '2022-05', totalCount: 0 },
  //   { groupDate: '2022-06', totalCount: 15 },
  // ];
  const noticeList = {
    title: t('GLOBAL_CONFIRM_DELETION'),
    mainText: t('GLOBAL_DELETE_DATA'),
    subText: '',
  };

  const alertDeleteKpiGraph = (idx: string) => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 570,
      title: '',
      content: (
        <AlertModalContent
          title={noticeList.title}
          mainText={noticeList.mainText}
          subText={noticeList.subText}
        />
      ),
      onClick() {
        const submitData = {
          idArr: [idx],
        };
        StatisticsActions.delete_statistics_graph_request(submitData);
      },
      onCancel() {
        return;
      },
    });
  };

  const handleDeleteChart = (idx: string) => {
    alertDeleteKpiGraph(idx);
  };

  useEffect(() => {
    periodRangeIndex.map((item: number, index: number) => {
      const dateRange = moment().subtract(item, 'months').format('YYYY-MM');
      const obj = {
        date: dateRange,
        week: null,
      };
      setPeriodMonthly(draft => draft.concat(obj));
    });
    periodRangeIndex.map((item: number, index: number) => {
      const currYear = moment().format('YYYY');
      const dateRange = moment().subtract(item, 'weeks').format('YYYY-MM-DD');
      const dateRangeWeekend = moment(dateRange).endOf('week').format('YYYY-MM-DD');
      const obj = {
        week: `${currYear}-${weekCount - item}`,
        date: dateRangeWeekend,
      };
      setPeriodWeekly(draft => draft.concat(obj));
    });
    dailyRangeIndex.map((item: number, index: number) => {
      const dateRange = moment().subtract(item, 'days').format('YYYY-MM-DD');
      const obj = {
        date: dateRange,
        week: null,
      };
      setPeriodDaily(draft => draft.concat(obj));
    });
  }, []);

  useEffect(() => {
    StatisticsActions.fetch_statistics_list_request();
    return () => {
      StatisticsActions.fetch_statistics_list_clear();
    };
  }, []);

  useDidUpdateEffect(() => {
    if (deleteStatisticSuccess) {
      StatisticsActions.fetch_statistics_list_request();
    }
  }, [deleteStatisticSuccess]);

  useDidUpdateEffect(() => {
    if (fetchStatisticsListSuccess) {
      setGraphCount(statisticsList?.length);
    }
  }, [fetchStatisticsListSuccess]);

  return (
    <StyledKpi data-component-name="Kpi">
      <div className="KpiContainer__title_box">
        <PageTitle title={t('NAV_MENU_KPI')} />
        {graphCount > 0 && (
          <PageSubTitle
            title={
              <Trans
                defaults="KPI_GRAPH_COUNT"
                components={[
                  <CustomSpan fontColor="#00a6e2">{{ graphCount }}</CustomSpan>,
                  <CustomSpan fontColor={oemState() === 'marathon' ? '#fff' : '#222'}></CustomSpan>,
                ]}
              />
            }
          />
        )}
      </div>
      {!!fetchStatisticsListPending ? (
        <div className="KpiContainer__kpi_progress">
          <CircularLoading size={40} />
        </div>
      ) : (
        <div className="KpiContainer__kpi_list_wrapper">
          {statisticsList?.length > 0 &&
            statisticsList.map((item: any, index: number) => {
              return (
                <div className="KpiContainer__kpi_graph_container" key={index}>
                  <KpiChartBox
                    language={language}
                    graphData={item}
                    periodRange={
                      item.duration === 'M'
                        ? periodMonthly
                        : item.duration === 'D'
                        ? periodDaily
                        : periodWeekly
                    }
                    isMilling={item._id.includes('motionapp-kpi')}
                    onDelete={handleDeleteChart}
                  />
                </div>
              );
            })}
          {statisticsList?.length < 8 && (
            <IconButton
              className={cx('KpiContainer__kpi_add_container', {
                isWide: statisticsList.length === 0,
              })}
              onClick={() => {
                history.push(pageUrl.kpi.add);
              }}
            >
              <div className="KpiContainer__kpi_add_button_box">
                <img src={icon_add_kpi} />
                <CustomSpan fontSize={20} fontWeight={500} fontColor="#888">
                  <T>KPI_ADD_GRAPH</T>
                </CustomSpan>
                <CustomSpan fontSize={16} fontWeight={400} fontColor="#888">
                  <T>KPI_ADD_GRAPH_MAX</T>
                </CustomSpan>
              </div>
            </IconButton>
          )}
        </div>
      )}
    </StyledKpi>
  );
}
const StyledKpi = styled.section<{}>`
  /* margin-top: 30px; */
  .KpiContainer__kpi_progress {
    width: 1640px;
    height: 670px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .KpiContainer__title_box {
    .KpiContainer__kpi_data {
      margin: 20px 0;
      font-size: 24px;
    }
  }
  .KpiContainer__kpi_list_wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-top: 30px;
    margin-bottom: 125px;
    row-gap: 24px;
    /* column-gap: 24px; */
    justify-content: space-between;
  }
  .KpiContainer__kpi_graph_container {
    background-color: #fff;
    /* width: 785px; */
    width: 800px;
    height: 830px;
    box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
    border-radius: 10px;
  }
  .KpiContainer__kpi_add_container {
    background-color: #fff;
    /* width: 785px; */
    width: 800px;
    height: 830px;
    border-radius: 10px;
    box-shadow: 0px 0px 10px rgb(0 0 0 / 16%);
    display: flex;
    align-items: center;
    justify-content: center;
    &.isWide {
      /* width: 1600px; */
      width: 100%;
      height: 670px;
    }
    .KpiContainer__kpi_add_button_box {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      row-gap: 30px;

      .KpiContainer__kpi_add_button {
        width: 120px;
        height: 120px;
        border-radius: 50%;
      }
    }
  }
`;
export default React.memo(KpiContainer);
